import React from 'react';
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Text,
  Spinner,
  useToast,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
} from '@chakra-ui/react';
import { FiUserPlus, FiLock, FiUnlock } from 'react-icons/fi';
import xhr from 'xhr';

export default function SchoolUsers({ schoolId }) {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [toggling, setToggling] = React.useState({});
  const [confirmUser, setConfirmUser] = React.useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const toast = useToast();

  const [newUser, setNewUser] = React.useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
  });

  React.useEffect(() => {
    fetchUsers();
  }, [schoolId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await xhr(`/schools/${schoolId}/users`);
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error fetching school users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password) {
      toast({ title: 'Campos requeridos', description: 'Email y contraseña son obligatorios', status: 'warning', position: 'bottom', duration: 4000, isClosable: true });
      return;
    }

    try {
      setCreating(true);

      // Get the school role id
      const rolesRes = await xhr(`/users-permissions/roles`);
      const schoolRole = (rolesRes.data?.roles || []).find((r) => r.type === 'school');

      if (!schoolRole) {
        toast({ title: 'Error de configuración', description: 'No se encontró el rol "school" en el sistema', status: 'error', position: 'bottom', duration: 5000, isClosable: true });
        return;
      }

      await xhr.post('/users', {
        username: newUser.email,
        email: newUser.email,
        password: newUser.password,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        role: schoolRole.id,
        school: schoolId,
        confirmed: true,
      });

      toast({ title: 'Usuario creado exitosamente', description: `Se creó el usuario con email ${newUser.email}`, status: 'success', position: 'bottom', duration: 4000, isClosable: true });
      setNewUser({ email: '', password: '', first_name: '', last_name: '' });
      onClose();
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al crear usuario';
      toast({ title: 'Error al crear usuario', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setCreating(false);
    }
  };

  const openConfirm = (user) => {
    setConfirmUser(user);
    onConfirmOpen();
  };

  const handleToggleBlock = async () => {
    if (!confirmUser) return;
    const user = confirmUser;
    onConfirmClose();
    setToggling((prev) => ({ ...prev, [user.id]: true }));
    try {
      const newBlocked = !user.blocked;
      await xhr.put(`/users/${user.id}`, { data: { blocked: newBlocked } });
      toast({
        title: newBlocked ? 'Usuario bloqueado' : 'Usuario desbloqueado',
        description: user.email,
        status: newBlocked ? 'warning' : 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
      fetchUsers();
    } catch (error) {
      toast({ title: 'Error al actualizar usuario', status: 'error', position: 'bottom', duration: 4000, isClosable: true });
    } finally {
      setToggling((prev) => ({ ...prev, [user.id]: false }));
      setConfirmUser(null);
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="sm">Usuarios Administradores</Heading>
        <Button leftIcon={<FiUserPlus />} colorScheme="purple" size="sm" onClick={onOpen}>
          Agregar Usuario
        </Button>
      </Flex>

      <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Nombre</Th>
              <Th>Email</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td>
                  {user.first_name || ''} {user.last_name || ''}
                </Td>
                <Td>{user.email}</Td>
                <Td>
                  {user.blocked ? (
                    <Badge colorScheme="red">Bloqueado</Badge>
                  ) : (
                    <Badge colorScheme={user.confirmed ? 'green' : 'yellow'}>
                      {user.confirmed ? 'Activo' : 'Pendiente'}
                    </Badge>
                  )}
                </Td>
                <Td>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme={user.blocked ? 'green' : 'red'}
                    leftIcon={user.blocked ? <FiUnlock /> : <FiLock />}
                    isLoading={toggling[user.id]}
                    onClick={() => openConfirm(user)}
                  >
                    {user.blocked ? 'Desbloquear' : 'Bloquear'}
                  </Button>
                </Td>
              </Tr>
            ))}
            {users.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" py={6} color="gray.500">
                  No hay usuarios admin para esta institución
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Modal: Confirmar Bloqueo/Desbloqueo */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{confirmUser?.blocked ? 'Desbloquear usuario' : 'Bloquear usuario'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {confirmUser?.blocked
                ? `¿Estás seguro de desbloquear a ${confirmUser?.email}? Podrá acceder nuevamente a la plataforma.`
                : `¿Estás seguro de bloquear a ${confirmUser?.email}? No podrá acceder a la plataforma hasta que sea desbloqueado.`}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose}>
              Cancelar
            </Button>
            <Button
              colorScheme={confirmUser?.blocked ? 'green' : 'red'}
              onClick={handleToggleBlock}
            >
              {confirmUser?.blocked ? 'Desbloquear' : 'Bloquear'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Crear Usuario */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nuevo Usuario Admin</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={3}>
              <FormControl>
                <FormLabel>Nombre</FormLabel>
                <Input
                  value={newUser.first_name}
                  onChange={(e) => setNewUser((p) => ({ ...p, first_name: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Apellido</FormLabel>
                <Input
                  value={newUser.last_name}
                  onChange={(e) => setNewUser((p) => ({ ...p, last_name: e.target.value }))}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Contraseña</FormLabel>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="purple" onClick={handleCreate} isLoading={creating}>
              Crear Usuario
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
